/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { string, bool, exact, arrayOf, number } from 'prop-types';

import { ButtonsGrid, UpcomingEvent, WhiteLabeling, FlexWithGradientBackground, Announcement } from '@atoms';
import {
  HomeGrid,
  LocationTeam,
  Services,
  BookedRooms,
  ReferFriend,
  DiscountModal,
  OnboardingBanner,
} from '@molecules';
import { GuestInvitation } from '@organisms';
import { updateUserSettings, setDiscountModalDisplayed } from '@store/user/duck';
import { Main } from '@templates';
import { theme } from '@styles';
import { Typography, Flex } from '@mixins';
import { getCloudinaryImageLinkBySize } from '@helpers/common';

import TestLocation from '@assets/images/TestLocation.jpg';
import TestLogo from '@assets/images/TestLogo.png';
import { storage } from '@utils';

const IMAGE_WIDTH = 564;
const BIG_IMAGE_WIDTH = 1680;

const Home = ({
  isOnboardingAvailable,
  isLoading,
  firstName,
  location,
  upcomingEvent,
  locationTeam,
  locationLongitude,
  locationLatitude,
  locationAddress,
  locationImage,
  wifiSettings,
  locationLogo,
  isWhiteLabelingOpen,
  announcement,
  headerAnnouncement,
  parsedHeaderAnnouncementSavedLocally,
  isDiscountAvailable,
  referFriendCurrency,
  referFriendAmount,
  onboardingBannerClosedAt,
}) => {
  const wasBannerClosedToday = useMemo(
    () => onboardingBannerClosedAt?.substring(0, 10) === new Date().toISOString().substring(0, 10),
    [onboardingBannerClosedAt],
  );

  const dispatch = useDispatch();

  const [isOnboardingBannerShown, setIsOnboardingBannerShown] = useState(!wasBannerClosedToday);
  const [isInvitationOpen, setIsInvitationOpen] = useState(false);
  const [isDiscountOpen, setIsDiscountOpen] = useState(isDiscountAvailable);

  useEffect(() => {
    setIsDiscountOpen(isDiscountAvailable);
  }, [isDiscountAvailable]);

  const { bookedRooms } = useSelector(store => store.bookingStore);
  const { onboardingState } = useSelector(store => store.companyStore);

  const handleInviteGuestsModalChangeState = useCallback(() => {
    setIsInvitationOpen(prevValue => !prevValue);
  }, []);

  const handleDiscountModalClose = () => {
    setIsDiscountOpen(false);
    dispatch(
      updateUserSettings({
        shouldDisplayDiscountModal: false,
      }),
    );
    dispatch(setDiscountModalDisplayed(true));
  };

  const handleBannerClose = useCallback(() => {
    setIsOnboardingBannerShown(false);
  }, [storage]);

  return (
    <>
      <WhiteLabeling
        location={location}
        isOpen={isWhiteLabelingOpen}
        image={getCloudinaryImageLinkBySize(locationImage, BIG_IMAGE_WIDTH)}
        logo={locationLogo}
      />
      <DiscountModal onModalClose={handleDiscountModalClose} isOpen={isDiscountOpen} />
      <Main
        withPaddingTop={false}
        withPaddingX={false}
        isWhiteLabelingOpen={isWhiteLabelingOpen}
        isLoading={isLoading}
        afterHeaderContent={headerAnnouncement?.content}
        afterHeaderContentId={headerAnnouncement?.id}
        parsedHeaderAnnouncementSavedLocally={parsedHeaderAnnouncementSavedLocally}>
        {isOnboardingBannerShown && isOnboardingAvailable && onboardingState?.currentStep && (
          <OnboardingBanner onClose={handleBannerClose} />
        )}
        <Flex width="100%" alignItems="center" maxWidth={1440} px={[20, 30, 40, 60]} flexDirection="column">
          {!!bookedRooms.length && <BookedRooms rooms={bookedRooms} />}
          <Flex
            flexDirection={['column', 'column', 'column', 'row']}
            mb={20}
            mt={30}
            width="100%"
            alignItems="start"
            justifyContent="space-between">
            <Typography lineHeight={1.25} variant="rightserif-400" fontSize={[30, 34]}>
              Hey
              <br />
              {firstName}!
            </Typography>
            <Flex py={18} alignSelf={['start', 'start', 'start', 'end']}>
              <ButtonsGrid onInviteGuests={handleInviteGuestsModalChangeState} />
            </Flex>
          </Flex>
          <Flex justifyContent="space-between" minHeight={['300px', '400px', '540px']} width="100%" mb={80}>
            {upcomingEvent ? (
              <UpcomingEvent eventDetails={upcomingEvent} />
            ) : (
              <FlexWithGradientBackground
                pointer
                mr={['4px', '7px']}
                imageUrl={getCloudinaryImageLinkBySize(locationImage, IMAGE_WIDTH)}
                width="50%"
                minHeight="100%"
                flexDirection="column"
                p={[15, 20, 35]}
                justifyContent="end">
                <Typography color={theme.color.white} variant="rightserif-400" fontSize={['20px', '30px', '40px']}>
                  {`Welcome to ${location}`}
                </Typography>
              </FlexWithGradientBackground>
            )}
            <HomeGrid />
          </Flex>
          {announcement?.id && (
            <Flex mb={80} width="100%">
              <Announcement title={announcement.title} content={announcement.content} url={announcement.url} />
            </Flex>
          )}
          {!!locationTeam.length && <LocationTeam team={locationTeam} locationName={location} />}
          <ReferFriend currency={referFriendCurrency} amount={referFriendAmount} />
        </Flex>
        <Services
          address={locationAddress}
          latitude={locationLatitude}
          longitude={locationLongitude}
          wifiSettings={wifiSettings}
        />
        {isInvitationOpen && <GuestInvitation onClose={handleInviteGuestsModalChangeState} isOpen={isInvitationOpen} />}
      </Main>
    </>
  );
};

Home.defaultProps = {
  isOnboardingAvailable: false,
  onboardingBannerClosedAt: '',
  isDiscountAvailable: false,
  isWhiteLabelingOpen: false,
  announcement: {},
  firstName: '',
  location: '',
  locationLatitude: 0,
  locationLongitude: 0,
  locationAddress: '',
  locationLogo: TestLogo,
  locationImage: TestLocation,
  upcomingEvent: {},
  locationTeam: [],
  wifiSettings: {},
  headerAnnouncement: {},
  parsedHeaderAnnouncementSavedLocally: {},
  referFriendCurrency: 'USD',
  referFriendAmount: '0',
};

Home.propTypes = {
  isOnboardingAvailable: bool,
  onboardingBannerClosedAt: string,
  isDiscountAvailable: bool,
  isLoading: bool.isRequired,
  firstName: string,
  location: string,
  upcomingEvent: exact({
    id: number,
    name: string,
    description: string,
    hasSlots: bool,
    locationDescription: string,
    upcomingId: number,
    upcomingDate: string,
    startTime: string,
    isRecurring: bool,
    endTime: string,
    location: string,
    imageUrl: string,
    locationId: number,
  }),
  locationTeam: arrayOf(
    exact({
      id: number,
      firstName: string,
      lastName: string,
      role: string,
      jobTitle: string,
      imageUrl: string,
    }),
  ),
  locationLatitude: number,
  locationLongitude: number,
  locationAddress: string,
  wifiSettings: exact({
    members: exact({
      network: string,
      password: string,
    }),
    guests: exact({
      network: string,
      password: string,
    }),
  }),
  locationLogo: string,
  isWhiteLabelingOpen: bool,
  locationImage: string,
  announcement: exact({
    id: number,
    type: string,
    title: string,
    content: string,
    url: string,
  }),
  headerAnnouncement: exact({
    id: number,
    type: string,
    title: string,
    content: string,
    url: string,
  }),
  parsedHeaderAnnouncementSavedLocally:
    exact({
      id: number || null,
      locationId: number || null,
    }) || {},
  referFriendCurrency: string,
  referFriendAmount: string,
};

export default Home;
